import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { GoldenParticles } from "@/components/GoldenParticles";
import { Countdown } from "@/components/Countdown";
import renataAsset from "@/assets/renata.asset.json";
import vslAsset from "@/assets/vsl.asset.json";
import printLarissa from "@/assets/print-larissa.asset.json";
import printBia from "@/assets/print-bia.asset.json";
import printCamila from "@/assets/print-camila.asset.json";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "O Código da Reconquista Silenciosa — Renata Caroline" },
      {
        name: "description",
        content:
          "Teste gratuito de 2 minutos revela o padrão emocional que te faz se esforçar mais e receber menos — e o que muda quando você para.",
      },
      { property: "og:title", content: "O Código da Reconquista Silenciosa" },
      {
        property: "og:description",
        content:
          "Descubra o padrão emocional invisível que opera em você — e como quebrá-lo.",
      },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Inter:wght@300;400;500;600;700&display=swap",
      },
    ],
  }),
  component: Landing,
});

const CHECKOUT_URL = "https://pay.kiwify.com.br/g7Y0X9v";
const WHATSAPP_URL =
  "https://wa.me/34634449155?text=Ol%C3%A1%21+O+resultado+do+quiz+me+fez+refletir+bastante.+Gostaria+de+entender+melhor+como+esse+m%C3%A9todo+pode+me+ajudar.";
const UNLOCK_SECONDS = 193;

const QUESTIONS = [
  {
    q: "Quando ele some ou te dá menos atenção, o que você faz primeiro?",
    a: [
      "Fico na minha e espero ele vir",
      "Mando uma mensagem perguntando se tá tudo bem",
      "Fico remoendo na minha cabeça o que pode ter acontecido",
      "Vou atrás, fico explicando o que sinto e tentando resolver",
    ],
  },
  {
    q: "Como você age quando sente que ele está mais distante?",
    a: [
      "Continuo sendo eu mesma, sem mudar nada",
      "Fico mais carinhosa pra ver se ele responde",
      "Fico analisando o que fiz de errado",
      "Faço tudo pra ele se sentir melhor e se aproximar de mim",
    ],
  },
  {
    q: "Quando há uma briga ou desentendimento, como você costuma agir?",
    a: [
      "Dou meu espaço e espero o momento certo",
      "Peço pra conversar quando os dois estiverem calmos",
      "Fico mal e fico repetindo a situação na cabeça",
      "Corro atrás pra resolver logo, mesmo que a culpa não seja minha",
    ],
  },
  {
    q: "Como você descreveria seu papel na maioria das suas relações?",
    a: [
      "Parceira — dou e recebo de forma equilibrada",
      "Cuidadora — gosto de apoiar e me sinto bem nisso",
      "Questionadora — sempre analisando o que está acontecendo",
      "Provedora — resolvo, cuido, sustento emocionalmente mais do que recebo",
    ],
  },
  {
    q: "O que você sente quando pensa em 'não fazer nada' numa situação difícil com ele?",
    a: [
      "Tranquilidade — sei que o tempo resolve",
      "Um certo desconforto, mas consigo segurar",
      "Ansiedade — fico pensando demais",
      "Angústia — sinto que preciso agir ou vai piorar",
    ],
  },
  {
    q: "Como você reage quando ele não reconhece o que você faz por ele?",
    a: [
      "Não mudo nada — faço porque quero, não para receber",
      "Fico um pouco chateada mas consigo deixar pra lá",
      "Fico magoada e fico pensando nisso por dias",
      "Me sinto invisível e ainda assim continuo fazendo mais, esperando ele perceber",
    ],
  },
];

type Stage = "hero" | "quiz" | "loading" | "revealed";

function Landing() {
  const [stage, setStage] = useState<Stage>("hero");
  const [qIdx, setQIdx] = useState(0);
  const [videoUnlocked, setVideoUnlocked] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const unlockedFlag = useRef(false);
  const rafRef = useRef<number | null>(null);

  const pickAnswer = () => {
    if (qIdx < QUESTIONS.length - 1) {
      setQIdx(qIdx + 1);
    } else {
      setStage("loading");
    }
  };

  // Loading → diagnostico
  useEffect(() => {
    if (stage !== "loading") return;
    const t = setTimeout(() => {
      setStage("revealed");
      setTimeout(() => {
        document
          .getElementById("diagnostico")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }, 3100);
    return () => clearTimeout(t);
  }, [stage]);

  // Video events → progresso fluido, loading, erro, buffering
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const updateProgress = () => {
      if (v.duration > 0 && Number.isFinite(v.duration)) {
        setVideoProgress(Math.min(100, (v.currentTime / v.duration) * 100));
      }
      if (!unlockedFlag.current && v.currentTime >= UNLOCK_SECONDS) {
        unlockedFlag.current = true;
        setVideoUnlocked(true);
      }
    };

    const tick = () => {
      updateProgress();
      rafRef.current = requestAnimationFrame(tick);
    };

    const onPlay = () => {
      setVideoPlaying(true);
      setVideoLoading(false);
      setVideoError(null);
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(tick);
    };
    const onPause = () => {
      setVideoPlaying(false);
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      updateProgress();
    };
    const onWaiting = () => setVideoLoading(true);
    const onPlaying = () => { setVideoLoading(false); setVideoError(null); };
    const onLoadedMeta = () => setVideoLoading(false);
    const onError = () => {
      setVideoError("Não foi possível carregar o vídeo. Verifique sua conexão e tente novamente.");
      setVideoLoading(false);
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
    const onSeeked = () => updateProgress();
    const onEnded = () => { setVideoPlaying(false); setVideoProgress(100); };

    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("waiting", onWaiting);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("loadedmetadata", onLoadedMeta);
    v.addEventListener("loadeddata", onLoadedMeta);
    v.addEventListener("error", onError);
    v.addEventListener("seeked", onSeeked);
    v.addEventListener("timeupdate", updateProgress);
    v.addEventListener("ended", onEnded);

    if (v.readyState >= 1) setVideoLoading(false);

    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("waiting", onWaiting);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("loadedmetadata", onLoadedMeta);
      v.removeEventListener("loadeddata", onLoadedMeta);
      v.removeEventListener("error", onError);
      v.removeEventListener("seeked", onSeeked);
      v.removeEventListener("timeupdate", updateProgress);
      v.removeEventListener("ended", onEnded);
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [stage]);

  const startQuiz = () => {
    setStage("quiz");
    setTimeout(() => {
      document
        .getElementById("quiz")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <main className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      {/* ───────── HERO ───────── */}
      <section className="relative min-h-screen overflow-hidden px-6 py-20">
        <GoldenParticles density={70} />
        <div className="relative mx-auto flex min-h-[80vh] max-w-4xl flex-col items-center justify-center text-center">
          <span className="eyebrow mb-6">O DIAGNÓSTICO EMOCIONAL</span>
          <h1 className="font-serif text-3xl leading-[1.15] text-foreground md:text-6xl">
            Você não está fazendo nada de errado.
            <br />
            <span className="italic text-gold">
              Você só não sabe (ainda) o que faz ele correr atrás de você.
            </span>
          </h1>
          <p className="mt-8 max-w-2xl text-base text-foreground/80 md:text-lg">
            Esse teste gratuito de 2 minutos revela o padrão exato que faz você
            se esforçar mais e receber menos — e o que muda quando você para de
            fazer isso.
          </p>
          <button onClick={startQuiz} className="btn-gold mt-10 text-base md:text-lg">
            Descobrir o que me faz continuar sem receber nada dele →
          </button>
          <p className="mt-4 text-xs text-foreground/60">
            Gratuito · 2 minutos · Diagnóstico personalizado
          </p>
        </div>
      </section>

      {/* ───────── QUIZ ───────── */}
      {(stage === "quiz" || stage === "loading" || stage === "revealed") && (
        <section id="quiz" className="relative px-6 py-20 md:py-28">
          <div className="mx-auto max-w-3xl">
            <div className="mb-10 text-center">
              <span className="eyebrow">O QUIZ EMOCIONAL</span>
              <p className="mt-4 text-foreground/80">
                Responda com sinceridade. Não existe resposta certa — existe a
                sua verdade.
              </p>
            </div>

            {stage === "quiz" && (
              <>
                <div className="mb-10">
                  <div className="mb-3 flex items-center justify-between text-xs tracking-[0.3em] text-gold/80">
                    <span>JORNADA EMOCIONAL</span>
                    <span>
                      {qIdx + 1} / {QUESTIONS.length}
                    </span>
                  </div>
                  <div className="h-[2px] w-full overflow-hidden rounded-full bg-gold/15">
                    <div
                      className="h-full bg-gradient-to-r from-[#c9a84c] to-[#e2c879] transition-all duration-500"
                      style={{
                        width: `${((qIdx + 1) / QUESTIONS.length) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                <div key={qIdx} className="fade-up">
                  <h3 className="mb-10 text-center font-serif text-2xl leading-tight md:text-4xl">
                    {QUESTIONS[qIdx].q}
                  </h3>
                  <div className="grid gap-4">
                    {QUESTIONS[qIdx].a.map((opt, i) => (
                      <button
                        key={i}
                        onClick={pickAnswer}
                        className="card-elegant w-full px-6 py-5 text-left text-base text-foreground/90 transition-all hover:translate-x-1 hover:text-foreground md:text-lg"
                      >
                        <span className="mr-4 font-serif text-gold">
                          {String.fromCharCode(65 + i)}.
                        </span>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {stage === "loading" && (
              <div className="py-20 text-center fade-up">
                <p className="mb-8 font-serif text-2xl italic text-foreground/90">
                  Analisando seu padrão emocional...
                </p>
                <div className="mx-auto h-[3px] w-full max-w-md overflow-hidden rounded-full bg-gold/15">
                  <div
                    className="h-full bg-gradient-to-r from-[#c9a84c] to-[#e2c879]"
                    style={{ animation: "loadbar 3s linear forwards" }}
                  />
                </div>
                <style>{`@keyframes loadbar { from { width: 0% } to { width: 100% } }`}</style>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ───────── DIAGNÓSTICO ───────── */}
      {stage === "revealed" && (
        <>
          <section
            id="diagnostico"
            className="relative px-6 py-20 md:py-28 fade-up"
          >
            <div className="mx-auto max-w-3xl">
              <div className="text-center">
                <span className="eyebrow">SEU DIAGNÓSTICO EMOCIONAL</span>
                <p className="mx-auto mt-6 max-w-2xl font-serif text-lg italic text-foreground/90 md:text-xl">
                  "Ser a provedora silenciosa significa carregar o mundo nas
                  costas enquanto todos acreditam que você está bem."
                </p>
                <p className="mt-8 text-foreground/80">
                  Existem alguns tipos de provedora silenciosa. Seu diagnóstico
                  revela:
                </p>
                <h2 className="mt-4 font-serif text-4xl text-gold md:text-6xl">
                  Você é uma Salvadora Devotada.
                </h2>
                <p className="mt-6 font-serif text-lg italic text-foreground/85 md:text-xl">
                  Você confunde o amor que sente com a missão de consertar — e
                  se perde no caminho.
                </p>
              </div>

              <div className="mt-12 space-y-6 text-foreground/85 leading-relaxed md:text-lg">
                <p>
                  Suas respostas mostram um padrão profundo de devoção: você se
                  culpa por coisas que não são suas, assume responsabilidade
                  pelo bem-estar emocional dele e mede o seu valor pelo quanto
                  consegue cuidar. Isso não é amor — é treinamento.
                </p>
                <p>
                  A Salvadora Devotada cresceu acreditando que precisa merecer
                  afeto. Por isso se entrega ao máximo — e fica em relações que
                  outras mulheres já teriam saído há tempos. Você enxerga
                  potencial onde ele só mostra padrão. Você dá colo onde ele
                  deveria estar dando esforço.
                </p>
                <p>
                  O problema é que homens não se apaixonam pelas mulheres que os
                  salvam. Eles se apoiam nelas. Há uma diferença gigante. E ela
                  explica por que você sente que dá tanto e recebe tão pouco em
                  troca.
                </p>
              </div>

              <hr className="gold-divider my-12" />

              <p className="mb-6 text-center text-foreground/85">
                O que muda quando você quebra esse padrão:
              </p>
              <ul className="grid gap-4 text-foreground/90">
                {[
                  "Você para de correr — e ele começa a buscar",
                  "Você aprende a diferença entre amar e carregar",
                  "Você reconstrói sua autoestima fora do papel de cuidadora",
                  "Você cria limites que ele respeita — sem precisar gritar",
                ].map((t) => (
                  <li key={t} className="flex gap-3">
                    <span className="text-gold">✦</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-14 text-center font-serif text-xl italic text-gold md:text-2xl">
                "Mulheres que quebraram esse padrão pararam de pedir atenção. E
                começaram a receber amor de verdade."
              </p>

            </div>
          </section>

          {/* ───────── VÍDEO DA RENATA ───────── */}
          <section className="relative px-6 pb-10 pt-6 md:pt-10">
            <div className="mx-auto max-w-3xl">
              <div className="mb-8 text-center">
                <span className="eyebrow">MENSAGEM DA RENATA PARA VOCÊ</span>
                <h3 className="mx-auto mt-5 max-w-2xl font-serif text-2xl leading-tight text-gold md:text-4xl">
                  Fica comigo durante 3 minutos que eu vou te explicar
                  exatamente o que fazer pra ele te dar tudo o que você quer{" "}
                  <span className="italic">imediatamente.</span>
                </h3>
                <p className="mt-4 font-serif italic text-foreground/85">
                  Assista até o final — é lá que tudo muda.
                </p>
              </div>

              <div
                className="group relative mx-auto w-full max-w-3xl cursor-pointer overflow-hidden rounded-2xl border border-gold/30 bg-black shadow-[0_20px_80px_-20px_rgba(201,168,76,0.35)]"
                onClick={() => {
                  const v = videoRef.current;
                  if (!v || videoError) return;
                  if (v.paused) {
                    const p = v.play();
                    if (p && typeof p.catch === "function") {
                      p.catch(() => setVideoError("Toque novamente para iniciar o vídeo."));
                    }
                    setVideoStarted(true);
                  } else {
                    v.pause();
                  }
                }}
              >
                <div className="relative aspect-video w-full">
                  <video
                    ref={videoRef}
                    src={vslAsset.url}
                    playsInline
                    preload="metadata"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                    className="absolute inset-0 h-full w-full object-contain"
                  />

                  {/* Overlay com botão de play */}
                  {!videoStarted && !videoError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 backdrop-blur-[1px]">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-gold bg-gold/20 transition-transform group-hover:scale-110 sm:h-20 sm:w-20 md:h-24 md:w-24">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="ml-1 h-8 w-8 text-gold sm:h-10 sm:w-10 md:h-12 md:w-12">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <p className="mt-4 px-4 text-center font-serif text-sm text-foreground sm:text-base md:text-lg">
                        Clique na tela — o vídeo vai começar
                      </p>
                    </div>
                  )}

                  {/* Spinner de loading/buffering */}
                  {videoStarted && videoLoading && !videoError && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/30 border-t-gold" />
                    </div>
                  )}

                  {/* Fallback de erro */}
                  {videoError && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 px-6 text-center">
                      <p className="font-serif text-sm text-foreground sm:text-base">{videoError}</p>
                      <button
                        className="rounded-md border border-gold/40 bg-gold/10 px-4 py-2 text-xs text-gold sm:text-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const v = videoRef.current;
                          if (!v) return;
                          setVideoError(null);
                          setVideoLoading(true);
                          v.load();
                          v.play().then(() => setVideoStarted(true)).catch(() =>
                            setVideoError("Ainda não foi possível carregar. Tente novamente em instantes."),
                          );
                        }}
                      >
                        Tentar novamente
                      </button>
                    </div>
                  )}
                </div>

                {/* Barra de progresso (sem tempo) — sempre visível após iniciar */}
                {videoStarted && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-2.5 bg-black/70 ring-1 ring-gold/20">
                    <div
                      className="h-full bg-gradient-to-r from-[#c9a84c] via-[#e2c879] to-[#f4dca0] shadow-[0_0_10px_rgba(226,200,121,0.55)] transition-[width] duration-100 ease-linear"
                      style={{ width: `${videoProgress}%` }}
                    />
                  </div>
                )}
              </div>

              {!videoUnlocked && videoStarted && (
                <p className="mt-5 text-center text-sm text-gold/90">
                  Assista até o final para desbloquear os resultados.
                </p>
              )}

              {videoUnlocked && (
                <p className="mt-6 text-center text-sm text-foreground/75">
                  Veja o que acontece quando o padrão quebra de verdade:
                </p>
              )}
            </div>
          </section>
        </>
      )}

      {/* ───────── DESBLOQUEADO (depois do vídeo) ───────── */}
      {stage === "revealed" && videoUnlocked && (
        <div className="fade-up">
          {/* PRINTS DE WHATSAPP */}
          <section className="px-6 py-16 md:py-20">
            <div className="mx-auto max-w-6xl">
              <div className="mb-12 text-center">
                <span className="eyebrow">RESULTADOS REAIS</span>
                <p className="mt-4 text-foreground/85 md:text-lg">
                  Mulheres com o mesmo padrão que já quebraram o ciclo:
                </p>
              </div>
              <div className="grid gap-6 md:grid-cols-3">
                {[
                  {
                    img: printCamila.url,
                    name: "Camila R.",
                    loc: "Minas Gerais",
                    quote:
                      "Chegou com buquê gigante numa sexta-feira comum. Sem data comemorativa.",
                  },
                  {
                    img: printLarissa.url,
                    name: "Larissa M.",
                    loc: "Rio de Janeiro",
                    quote:
                      "Ele que pediu pra jantar fora, pagou tudo e ainda tirou a foto pra eu postar.",
                  },
                  {
                    img: printBia.url,
                    name: "Beatriz A.",
                    loc: "São Paulo",
                    quote:
                      "Ele organizou uma viagem surpresa. Sozinho. Sem eu pedir nada.",
                  },
                ].map((t) => (
                  <div key={t.name} className="card-elegant overflow-hidden">
                    <img
                      src={t.img}
                      alt={`Print de WhatsApp de ${t.name}`}
                      className="w-full"
                    />
                    <div className="p-5">
                      <p className="font-serif text-gold">
                        {t.name} · <span className="text-gold/70">{t.loc}</span>
                      </p>
                      <p className="mt-2 text-sm italic text-foreground/85">
                        "{t.quote}"
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* DEPOIMENTOS ESCRITOS */}
          <section className="px-6 py-16 md:py-20">
            <div className="mx-auto max-w-6xl">
              <div className="grid gap-6 md:grid-cols-3">
                {[
                  {
                    t: "Eu mandava textão todo dia tentando explicar o que eu sentia. Ele sumia, eu corria atrás, ele voltava — e nada mudava. Depois que entendi meu padrão e aprendi a me posicionar diferente, ele que começou a me ligar. Em três semanas a dinâmica virou completamente.",
                    n: "Sandra T.",
                    l: "Los Angeles, Califórnia",
                  },
                  {
                    t: "Eu achava que estava fazendo tudo certo — cuidava, apoiava, estava sempre disponível. Não entendia por que ele não me valorizava. Quando parei de resolver tudo por ele, ele começou a me buscar de um jeito que nunca tinha acontecido antes.",
                    n: "Ana F.",
                    l: "Rio de Janeiro",
                  },
                  {
                    t: "Aprendi que minha energia de Salvadora Devotada estava afastando exatamente o que eu queria. Hoje me relaciono de um lugar completamente diferente. Recebo atenção, presentes, cuidado — sem pedir nada disso.",
                    n: "Pamela S.",
                    l: "São Paulo",
                  },
                ].map((d) => (
                  <div key={d.n} className="card-elegant p-6">
                    <p className="font-serif text-3xl text-gold/60">"</p>
                    <p className="text-foreground/85">{d.t}</p>
                    <p className="mt-5 text-sm text-gold">
                      — {d.n} ·{" "}
                      <span className="text-foreground/60">{d.l}</span>
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-12 text-center">
                <a href="#oferta" className="btn-gold">
                  QUERO O MEU RESULTADO TAMBÉM →
                </a>
              </div>
            </div>
          </section>

          {/* SOBRE RENATA */}
          <section className="px-6 py-16 md:py-24">
            <div className="mx-auto grid max-w-5xl items-center gap-12 md:grid-cols-[260px_1fr]">
              <div className="mx-auto">
                <img
                  src={renataAsset.url}
                  alt="Renata Caroline"
                  className="h-[220px] w-[220px] rounded-full border-2 border-gold object-cover shadow-[0_10px_40px_-10px_rgba(201,168,76,0.55)] md:h-[260px] md:w-[260px]"
                />
              </div>
              <div>
                <span className="eyebrow">SOBRE</span>
                <h2 className="mt-3 font-serif text-3xl md:text-5xl">
                  Renata Caroline
                </h2>
                <p className="mt-2 text-xs tracking-[0.25em] text-gold">
                  ESPECIALISTA EM DESENVOLVIMENTO EMOCIONAL FEMININO
                </p>
                <div className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
                  <p>
                    Renata Caroline não é coach de relacionamentos. É uma
                    mulher que viveu o padrão Salvadora Devotada por anos — e
                    que depois de quebrá-lo, documentou cada passo do processo.
                  </p>
                  <p>
                    Hoje ajuda mulheres a entenderem que o problema nunca foi
                    falta de amor, nem falta de beleza, nem falta de esforço.
                    Foi um padrão emocional invisível que pode ser
                    identificado, compreendido e reescrito.
                  </p>
                </div>
                <a
                  href="https://instagram.com/renatahcaroline"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-block text-gold underline-offset-4 hover:underline"
                >
                  @renatahcaroline
                </a>
              </div>
            </div>
          </section>

          {/* MÓDULOS */}
          <section className="px-6 py-16 md:py-24">
            <div className="mx-auto max-w-6xl">
              <div className="mb-14 text-center">
                <span className="eyebrow">ACESSO COMPLETO</span>
                <h2 className="mt-4 font-serif text-3xl md:text-5xl">
                  O que você recebe no Código da Reconquista Silenciosa
                </h2>
                <p className="mt-5 text-foreground/80 md:text-lg">
                  Seis etapas. Uma transformação completa.
                  <br />
                  Do padrão que te mantém invisível até a mulher que ele não
                  consegue parar de pensar.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                {[
                  [
                    "01",
                    "A Essência da Mulher que Ele Deseja",
                    "Antes de mudar qualquer comportamento, você precisa entender o que realmente desperta desejo em um homem — e por que tudo que você aprendeu sobre isso está errado. Essa aula muda a base.",
                  ],
                  [
                    "02",
                    "A Desprogramação",
                    "Seu padrão não é falha de caráter. É um código instalado sem você perceber. Aqui você o vê com clareza — e para de se sabotar sem saber que está fazendo isso.",
                  ],
                  [
                    "03",
                    "A Reconfiguração",
                    "Você reconhece o padrão. Agora é hora de desmontar cada peça. Como sua postura, sua energia e sua presença mudam quando você para de operar no automático.",
                  ],
                  [
                    "04",
                    "A Instalação",
                    "Novos comportamentos, nova forma de se mover no relacionamento. Não é técnica — é identidade. Você instala uma nova forma de existir que ele sente antes de você abrir a boca.",
                  ],
                  [
                    "05",
                    "A Implantação",
                    "O método entra em ação no dia a dia. Como agir nas situações reais — nas brigas, nos silêncios, nos sumiços — de um jeito que inverte a dinâmica sem drama e sem perda de dignidade.",
                  ],
                  [
                    "06",
                    "A Reacendida",
                    "Você não está aqui só para ele te notar de novo. Está aqui para nunca mais precisar ser reconquistada. Essa etapa é sobre a mulher que emerge depois — e que nunca mais volta para o padrão anterior.",
                  ],
                ].map(([n, t, d]) => (
                  <div key={n} className="card-elegant p-7">
                    <div className="mb-3 font-serif text-5xl text-gold">{n}</div>
                    <h3 className="font-serif text-xl text-foreground md:text-2xl">
                      {t}
                    </h3>
                    <p className="mt-3 text-foreground/75">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* BÔNUS */}
          <section className="px-6 py-16 md:py-20">
            <div className="mx-auto max-w-6xl">
              <div className="mb-12 text-center">
                <span className="eyebrow">BÔNUS EXCLUSIVOS</span>
                <p className="mt-4 text-foreground/85 md:text-lg">
                  4 Bônus Gratuitos — somente neste acesso.
                </p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                {[
                  [
                    "Bônus 01",
                    "R$97",
                    "Ímã Masculino",
                    "Comportamentos específicos que aumentam sua presença e despertam interesse genuíno — não superficial.",
                  ],
                  [
                    "Bônus 02",
                    "R$97",
                    "Quando Ele Está com Outra",
                    "O que fazer — e o que jamais fazer — quando a dor é essa. Como sair do modo desespero e entrar no único modo que funciona de verdade quando a situação é essa.",
                  ],
                  [
                    "Bônus 03",
                    "R$97",
                    "Decodificando o Orgulho Masculino",
                    "Como ler os sinais que ele manda — inclusive os silêncios — e saber exatamente quando agir e quando ficar na sua.",
                  ],
                  [
                    "Bônus 04",
                    "R$67",
                    "Como Parar de Dar Demais",
                    "O guia prático para desenvolver limites que ele respeita — sem brigar, sem ultimato, sem drama.",
                  ],
                ].map(([tag, val, title, desc]) => (
                  <div key={title} className="card-elegant p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs tracking-[0.25em] text-gold">
                        {tag}
                      </span>
                      <span className="text-sm text-foreground/60">
                        valor <span className="line-through">{val}</span>
                      </span>
                    </div>
                    <h3 className="mt-3 font-serif text-xl text-foreground">
                      {title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/75">{desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl border-2 border-gold bg-card p-7 text-center">
                <span className="text-xs tracking-[0.25em] text-gold">
                  COMUNIDADE EXCLUSIVA · valor <span className="line-through">R$197</span>
                </span>
                <h3 className="mt-3 font-serif text-2xl text-foreground">
                  Suporte direto e troca com outras mulheres em transformação.
                </h3>
                <p className="mt-2 text-foreground/80">
                  Você não faz esse processo sozinha.
                </p>
              </div>
            </div>
          </section>

          {/* OFERTA */}
          <section id="oferta" className="px-6 py-20 md:py-28">
            <div className="mx-auto max-w-3xl">
              <div className="space-y-5 text-center text-foreground/85 md:text-lg">
                <p>
                  Você já sabe que o problema não é você. Agora precisa decidir
                  se vai continuar operando no mesmo padrão esperando um
                  resultado diferente — ou se vai fazer algo diferente hoje.
                </p>
                <p>
                  Cada dia que o padrão Salvadora Devotada continua ativo, você
                  está treinando ele — e todo mundo ao redor — a te tratar com
                  menos. Não porque você merece menos. Porque o padrão está
                  mandando esse sinal no lugar de você.
                </p>
              </div>

              <div className="mt-12 rounded-2xl border border-gold/40 bg-card p-8 text-center md:p-12">
                <p className="text-sm text-foreground/70">
                  Valor total dos módulos + bônus + comunidade:
                </p>
                <p className="mt-2 text-2xl text-foreground/50 line-through">
                  R$772,00
                </p>
                <p className="mt-6 text-sm text-foreground/80">
                  Acesso imediato por apenas
                </p>
                <p className="mt-2 font-serif text-6xl text-gold md:text-7xl">
                  R$247,00
                </p>
                <p className="mt-2 text-foreground/70">ou 12x de R$25,55</p>

                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-foreground/75">
                  <span>🔒 Compra 100% segura</span>
                  <span>⚡ Acesso imediato</span>
                  <span>🛡️ Parcele em até 12x</span>
                </div>
              </div>

              {/* GARANTIA */}
              <div className="mx-auto mt-10 max-w-[480px] rounded-2xl border-2 border-double border-gold bg-card p-8 text-center">
                <div className="text-4xl">🛡️</div>
                <h3 className="mt-3 font-serif text-2xl font-bold text-gold">
                  Garantia Incondicional de 7 Dias
                </h3>
                <p className="mt-3 text-sm text-foreground/85">
                  Se em 7 dias você sentir que esse método não é para você,
                  basta enviar um e-mail e devolvemos 100% do seu investimento.
                  Sem perguntas. Sem burocracia. Sem julgamento.
                </p>
                <p className="mt-4 text-sm italic text-gold">
                  O risco é todo nosso. A transformação é toda sua.
                </p>
              </div>

              {/* URGÊNCIA */}
              <div className="mt-14">
                <div className="mb-6 inline-block rounded-full bg-[#7a1a2e] px-5 py-2 text-xs font-semibold tracking-[0.25em] text-foreground">
                  ⚠ POR QUE AGIR AGORA E NÃO DEPOIS?
                </div>
                <div className="space-y-4 text-foreground/85 md:text-lg">
                  <p>
                    Porque "depois" não existe. Existe o padrão continuando a
                    operar enquanto você pensa.
                  </p>
                  <p>
                    A mulher que você quer ser não nasce de uma decisão que
                    você adia. Ela nasce exatamente do momento em que você
                    decide — com medo ou sem medo.
                  </p>
                  <p>
                    Este acesso, com todos os bônus e a comunidade, está
                    disponível neste valor somente enquanto você estiver nesta
                    página.
                  </p>
                </div>

                <div className="mt-10">
                  <Countdown />
                  <p className="mt-4 text-center text-sm italic text-foreground/70">
                    Esta oferta expira em:
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-12 flex flex-col items-center gap-4">
                <a
                  href={CHECKOUT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold w-full max-w-md text-center md:text-lg"
                >
                  QUERO QUEBRAR MEU PADRÃO AGORA →
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost-gold w-full max-w-md"
                  style={{ color: "#f5f0eb", borderColor: "#f5f0eb" }}
                >
                  Prefiro conversar e entender melhor se isso é pra mim →
                </a>
              </div>
            </div>
          </section>

          {/* ENCERRAMENTO */}
          <section
            className="px-6 py-24"
            style={{
              background:
                "linear-gradient(180deg, #0a0608 0%, #000000 100%)",
            }}
          >
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-serif text-3xl text-foreground md:text-5xl">
                A mulher que aprende a se valorizar
              </h2>
              <p className="mt-3 font-serif text-4xl italic text-gold md:text-6xl">
                passa a ser escolhida.
              </p>
            </div>
          </section>
        </div>
      )}

      {/* ───────── RODAPÉ ───────── */}
      <footer className="border-t border-gold/15 bg-background px-6 py-8 text-center">
        <p className="text-xs tracking-[0.25em] text-foreground/60">
          © 2026 O CÓDIGO DA RECONQUISTA SILENCIOSA · RENATA CAROLINE
        </p>
      </footer>
    </main>
  );
}
